import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { useCallback } from "react";
import { useProduct } from "contexts/ProductContext";
import { useProductActions } from "hooks/useProductActions";
import { actionInProgress, actionErrored } from "utils/status";
import { copyToClipboard } from "utils/helpers";
import { toast } from "utils/toast";
import { useAnalytics } from "contexts/AnalyticsContext";
import { EmptyText } from "components/empty/EmptyText";
import { logError } from "shared/services/ErrorReporting";
import { getDescription } from "reedy-data";
import { Editor } from "pages/ProductDetails/tabs/BookDescriptionTab/components/Editor";
import {
  getKeywordsByType,
  keywordsToText,
  getDescriptionActions,
} from "integrations/firebase/collections";
import { BasePanel } from "components/BasePanel";
import { LoadingPanel, ErrorPanel, PanelFooter } from "components/panels";
import { generationErrorMessage } from "utils/errors";
import { WordCounter } from "components/indicators/WordCounter";
import { KeywordCounter } from "components/indicators/KeywordCounter";

export const DescriptionTab = () => {
  const { t } = useTranslation(["manuscriptDetails", "general"]);
  const { product } = useProduct();
  const { gaEvent } = useAnalytics();
  const { generateProductDescription } = useProductActions();

  // Get the default description hash
  const defaultDescription = product?.descriptions?.[0];
  const defaultDescriptionHash = defaultDescription?.hash;

  // Get the description text
  const selectedDescription = getDescription(product, defaultDescriptionHash);
  const description = selectedDescription?.textGenerated || "";

  // Get keywords for highlighting in the editor
  const { final } = getKeywordsByType(product);
  const keywords = {
    final: keywordsToText(final),
  };

  // Get description actions and check status
  const actions = getDescriptionActions(product, defaultDescriptionHash);
  const action = actions?.generate;
  const isGenerating = actionInProgress(action);
  const isError = actionErrored(action);
  const isEmpty = !description;

  const wordCount = description ? description.split(/\s+/).length : 0;

  // Calculate total keywords from raw data
  const totalKeywords = final?.length || 0;

  // Calculate found keywords using the processed keyword strings
  const foundKeywords = description
    ? keywords.final.filter((keyword) =>
        description.toLowerCase().includes(keyword.toLowerCase()),
      ).length
    : 0;

  const handleGenerateDescription = async () => {
    if (!product || !defaultDescriptionHash) return;

    try {
      await generateProductDescription(
        product,
        defaultDescriptionHash,
        null,
        null,
      );
    } catch (e) {
      logError(e);
      toast.error(t("manuscriptDetails:description.error.generate"));
    }
  };

  const handleCopy = useCallback(() => {
    if (!description) return;

    gaEvent({
      type: "click",
      payload: {
        analytics_id: "button_copy_description",
        manuscript_file_name: product?.manuscript?.filename ?? "",
        word_count: wordCount,
      },
    });

    copyToClipboard({ arrayToCopy: [description] });
    toast.success(t("manuscriptDetails:description.toasts.copy.success"));
  }, [description, gaEvent, product, t, wordCount]);

  // Create the footer element or statistics
  const footerElement = !isEmpty && !isGenerating && !isError && (
    <PanelFooter justify="end" padding="p-4 pr-12">
      <div className="flex space-x-6 align-center">
        <KeywordCounter found={foundKeywords} total={totalKeywords} />
        <WordCounter count={wordCount} />
      </div>
    </PanelFooter>
  );

  // If generating, show loading panel
  if (isGenerating) {
    return (
      <LoadingPanel
        title={t("manuscriptDetails:description.title")}
        loadingText={t("manuscriptDetails:description.generating")}
      />
    );
  }

  // If error, show error panel
  if (isError) {
    const errorMsg =
      generationErrorMessage(t, action?.statusCode) ||
      t("manuscriptDetails:description.error.generation", {
        code: action?.statusCode,
      });

    return (
      <ErrorPanel
        title={t("manuscriptDetails:description.title")}
        errorTitle={t("manuscriptDetails:description.error.title")}
        errorMessage={errorMsg}
        buttons={[
          <Button
            key="retry"
            label={t("general:buttons.retry")}
            onClick={handleGenerateDescription}
            icon="sparkles"
            size="small"
            analyticsId="button_retry_description"
          />,
        ]}
      />
    );
  }

  return (
    <BasePanel
      title={t("manuscriptDetails:description.title")}
      isEmpty={isEmpty}
      emptyComponent={
        <EmptyText
          title={t("manuscriptDetails:description.empty.title")}
          text={t("manuscriptDetails:description.empty.description")}
          buttons={[
            <Button
              key="generate"
              label={t("manuscriptDetails:description.generate")}
              onClick={handleGenerateDescription}
              icon="sparkles"
              size="small"
              analyticsId="button_generate_description"
            />,
          ]}
        />
      }
      headerAction={
        <div className="flex gap-2">
          {!isEmpty && (
            <Button
              label={t("general:buttons.copy")}
              onClick={handleCopy}
              icon="copy"
              variant="ghost"
              size="small"
              analyticsId="button_copy_description"
              className="text-primary-500"
            />
          )}
        </div>
      }
      footer={footerElement}
    >
      <Editor
        key={`${product?.id}-${defaultDescriptionHash}`}
        initialValue={description}
        keywords={keywords}
        disabled
        unescapeHtml
      />
    </BasePanel>
  );
};
